
// region JS Imports

// region External Modules
import { EventEmitter } from "events"
import Promise from "plugins/bluebird/index"

import u from "umbrellajs"
// endregion External Modules

// Private Modules

import {
  isPresentString,
} from "app/utils/type_checking"

import {
  refreshAndUpdateComponentPromise,
} from "app/authentication/remote_state"

import {
  captureMessage,
  captureException,
} from "app/error_reporting/index"

import {
  getContactInfoData,
  setContactInfoData,
} from "app/local_storages/contact_info_storage/index"

import {
  create as create_user,
  ResultTypeEnums as CreateUserResultTypes,
} from "app/resources/users/create/index"

import Auth_action from "app/authentication/action"
import Auth_callback_queue from "app/authentication/callback_queue"
import {
  isSignedIn,
} from "app/authentication/state"
import {
  trackSignUp,
} from "app/authentication/tracking"
import {
  getLastContactInfoSyncPromise,
} from "app/authentication/contact_info_caching"

import {
  create as create_building_user_shortlisting,
  ResultTypeEnums as CreateBuildingUserShortlistingResultTypes,
} from "app/resources/building_user_shortlistings/create"

import {
  destroy as destroy_building_user_shortlisting,
  ResultTypeEnums as DestroyBuildingUserShortlistingResultTypes,
} from "app/resources/building_user_shortlistings/destroy"
// Private Modules

// region Public Components
// endregion Public Components

// region Private Components
// endregion Private Components

// endregion JS Imports


const RejectionReasons = {
  ERROR_REPORTED: "ERROR_REPORTED",
}

const SOURCE_FILE_PATH = "apps/spacious_website/components/buildings/shortlist_button/icon_only/factory"


interface Inputs {
  container: HTMLElement,

  shortlisted_resource_ids: string[],
}

interface Selector {
  container: HTMLElement,

  shortlist_button: string,
  unshortlist_button: string,
}

const DEFAULT_SELECTOR = {
  shortlist_button:    ".cell--buildings--shortlist-button--icon-only__link--shortlist",
  unshortlist_button:  ".cell--buildings--shortlist-button--icon-only__link--unshortlist",
}

class ShortlistButton extends EventEmitter {
  private readonly selector: Selector

  private readonly container_el: HTMLElement
  private readonly building_id: any

  private readonly $shortlist_button: UmbrellaInstance
  private readonly $unshortlist_button: UmbrellaInstance

  constructor(inputs: Inputs) {
    super()

    this.selector = Object.assign({}, DEFAULT_SELECTOR, inputs)

    this.container_el = this.selector.container
    const u_container_el = u(this.container_el)

    this.building_id = u_container_el.data("building-id")

    this.$shortlist_button =
      u_container_el.find(this.selector.shortlist_button)
    this.$unshortlist_button =
      u_container_el.find(this.selector.unshortlist_button)

    this.bind_action()

    if (inputs.shortlisted_resource_ids.includes(this.building_id)) {
      this.displayAsShortlisted()
    }
  }

  private bind_action(){
    const $shortlist_button = this.$shortlist_button
    const $unshortlist_button = this.$unshortlist_button

    $shortlist_button
    .on("click", (e) => {
      e.preventDefault()

      this.onShortlistClick()
    })

    $unshortlist_button
    .on("click", (e) => {
      e.preventDefault()

      this.onUnshortlistClick()
    })

    return this
  }

  private onShortlistClick() {
    const local_contact_info_data = getContactInfoData()

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      Auth_callback_queue.add(() => {
        getLastContactInfoSyncPromise()
        .then(() => this.onShortlistClick())
        .done()
      }, "event:shortlist_building")

      Auth_action.showSignUpModal({
        enable_account_already_exists_error_message: false,
      })
      .then(({$modal, authentication_box}) => {
        authentication_box.on("sign_up:fail:account_conflict", ({email_address}) => {
          $modal.modal("hide")
          // Retry AFTER setting local contact email
          setContactInfoData({
            contact_email_address: email_address,
          })
          this.onShortlistClick()
        })
      })
      .done()
      return
    }

    let create_building_user_shortlisting_inputs = {}
    Promise.resolve()
    .then(() => {
      if (isSignedIn()) {
        return Promise.resolve()
      }

      return create_user({
        authentication_handle:  local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",
        email_address:          local_contact_info_data.contact_email_address || "",
        sign_in_on_success:     true,
      })
      .then(({result_type}) => {
        switch (result_type) {
          case CreateUserResultTypes.SUCCESS:
            trackSignUp()
            refreshAndUpdateComponentPromise().done()
            return
          case CreateUserResultTypes.CONFLICT:
            return
          default:
            captureMessage(
              `unexpected/unhandled result_type: <${result_type}>`,
              {
                source_file_path:
                  SOURCE_FILE_PATH,
              }
            )
            throw RejectionReasons.ERROR_REPORTED
        }
      })
    })
    .then(() => {
      const inputs = {
        // Need `||` to make flow happy
        user_authentication_handle: local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",

        building_id: this.building_id,
      }
      create_building_user_shortlisting_inputs = inputs
      return create_building_user_shortlisting(inputs)
    })
    .then(({result_type}) => {
      switch (result_type) {
        case CreateBuildingUserShortlistingResultTypes.SUCCESS:
          return
        default:
          captureMessage(
            `unexpected/unhandled result_type from create_building_user_shortlisting: <${result_type}>`,
            Object.assign({
              "source_file_path":
                SOURCE_FILE_PATH,
              "local_contact_info_data.contact_email_address":
                local_contact_info_data.contact_email_address || "unknown",
            }, create_building_user_shortlisting_inputs),
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then(() => {
      this.displayAsShortlisted()
    })
    .catch((whatever) => {
      if (whatever === RejectionReasons.ERROR_REPORTED) { return }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
            SOURCE_FILE_PATH,
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
    })
    .done()
  }

  private onUnshortlistClick() {
    const local_contact_info_data = getContactInfoData()

    if (! isPresentString(local_contact_info_data.contact_email_address)) {
      // Do nothing but just pretend to have unfollow
      this.displayAsNotShortlisted()
      return
    }

    Promise.resolve()
    .then(() => {
      return destroy_building_user_shortlisting({
        // Need `||` to make flow happy
        user_authentication_handle: local_contact_info_data.user_authentication_handle || local_contact_info_data.contact_email_address || "",

        building_id: this.building_id,
      })
    })
    .then(({result_type}) => {
      switch (result_type) {
        case DestroyBuildingUserShortlistingResultTypes.SUCCESS:
          return
        default:
          captureMessage(
            `unexpected/unhandled result_type from destroy_building_user_shortlisting: <${result_type}>`,
            {
              source_file_path:
              SOURCE_FILE_PATH,
            }
          )
          throw RejectionReasons.ERROR_REPORTED
      }
    })
    .then(() => {
      this.displayAsNotShortlisted()
    })
    .catch((whatever) => {
      if (whatever === RejectionReasons.ERROR_REPORTED) { return }

      if (whatever instanceof Error) {
        captureException(
          whatever,
          {
            source_file_path:
            SOURCE_FILE_PATH,
          }
        )
        return
      }

      captureMessage(
        whatever.toString(),
        {
          source_file_path:
          SOURCE_FILE_PATH,
        }
      )
    })
    .done()
  }

  private displayAsShortlisted() {
    this.container_el.classList.add("shortlisted")
  }
  private displayAsNotShortlisted() {
    this.container_el.classList.remove("shortlisted")
  }
}

const Factory = {
  create(selector: Inputs){
    return new ShortlistButton(selector)
  },
}

export {
  Factory,
}
